import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

type Mode = "login" | "forgot-email" | "forgot-code";

const Auth = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<Mode>("login");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const hasRecoveryParams = () => {
      const hash = window.location.hash || "";
      const search = window.location.search || "";
      if (hash.includes("type=recovery")) return true;
      const params = new URLSearchParams(search);
      if (params.get("type") === "recovery" && params.get("token_hash")) return true;
      return false;
    };

    const forwardToReset = () => {
      const target = `/reset-password${window.location.search}${window.location.hash}`;
      try {
        window.history.replaceState(null, "", window.location.pathname);
      } catch {
        // ignore
      }
      navigate(target, { replace: true });
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY") {
        forwardToReset();
        return;
      }
      if (event === "SIGNED_IN" && session) {
        if (hasRecoveryParams()) {
          forwardToReset();
          return;
        }
        const needsPasswordChange = session.user.user_metadata?.needs_password_change;
        if (needsPasswordChange) {
          navigate("/reset-password");
        } else {
          navigate("/portal");
        }
      }
    });

    const checkUser = async () => {
      if (hasRecoveryParams()) {
        forwardToReset();
        return;
      }
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const needsPasswordChange = session.user.user_metadata?.needs_password_change;
        if (needsPasswordChange) {
          navigate("/reset-password");
        } else {
          navigate("/portal");
        }
      }
    };
    checkUser();

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;

      if (data.session?.user.user_metadata?.needs_password_change) {
        toast.info("Please set a new password.");
        navigate("/reset-password");
      } else {
        toast.success("Logged in successfully!");
        navigate("/portal");
      }
    } catch (error: any) {
      toast.error(error.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const sendResetCode = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email);
      if (error) throw error;
      toast.success("Check your email for a verification code.");
      setMode("forgot-code");
    } catch (error: any) {
      toast.error(error.message || "Could not send code");
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    await sendResetCode();
  };

  const handleVerifyAndReset = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedCode = code.replace(/\D/g, "");
    if (trimmedCode.length < 4) {
      toast.error("Enter the verification code from your email");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        email,
        token: trimmedCode,
        type: "recovery",
      });
      if (verifyError) {
        if (verifyError.message?.toLowerCase().includes("expired")) {
          toast.error("Code expired. Click 'Resend code' to get a new one.");
        } else {
          toast.error("Invalid code. Double-check the code from your email.");
        }
        setLoading(false);
        return;
      }


      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
        data: { needs_password_change: false },
      });
      if (updateError) throw updateError;

      toast.success("Password updated! You're signed in.");
      navigate("/portal");
    } catch (error: any) {
      toast.error(error.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const backToLogin = () => {
    setMode("login");
    setCode("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const titles: Record<Mode, { title: string; desc: string }> = {
    "login": { title: "Member Login", desc: "Sign in to access your member portal" },
    "forgot-email": { title: "Reset Password", desc: "Enter your email — we'll send you a verification code" },
    "forgot-code": { title: "Enter Your Code", desc: `We sent a verification code to ${email}. Enter it below along with your new password.` },
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 py-20">
        <div className="max-w-md mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>{titles[mode].title}</CardTitle>
              <CardDescription>{titles[mode].desc}</CardDescription>
            </CardHeader>
            <CardContent>
              {mode === "login" && (
                <>
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password">Password</Label>
                      <Input
                        id="password"
                        type="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={6}
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading ? "Signing in..." : "Sign In"}
                    </Button>
                  </form>
                  <div className="mt-4 text-center">
                    <button
                      type="button"
                      onClick={() => setMode("forgot-email")}
                      className="text-sm text-primary hover:underline"
                    >
                      Forgot your password?
                    </button>
                  </div>
                </>
              )}

              {mode === "forgot-email" && (
                <form onSubmit={handleSendCode} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    If your admin just sent you a temporary password, use it on the Sign In screen instead — don't request a reset code.
                  </p>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? "Sending..." : "Send Code"}
                  </Button>
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={backToLogin}
                      className="text-sm text-primary hover:underline"
                    >
                      Back to login
                    </button>
                  </div>
                </form>
              )}

              {mode === "forgot-code" && (
                <form onSubmit={handleVerifyAndReset} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="code">Verification Code</Label>
                    <Input
                      id="code"
                      type="text"
                      inputMode="numeric"
                      pattern="\d*"
                      autoComplete="one-time-code"
                      placeholder="Enter the code from your email"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 10))}
                      className="text-center text-lg tracking-widest font-mono"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="newPassword">New Password</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={6}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={6}
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? "Resetting..." : "Reset Password"}
                  </Button>
                  <div className="flex justify-between text-sm">
                    <button
                      type="button"
                      onClick={sendResetCode}
                      disabled={loading}
                      className="text-primary hover:underline disabled:opacity-50"
                    >
                      Resend code
                    </button>
                    <button
                      type="button"
                      onClick={backToLogin}
                      className="text-primary hover:underline"
                    >
                      Back to login
                    </button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Auth;
